import { MARKET_SHARE_COLORS, MARKET_SHARE_COLORS_OTHER } from "@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants";
import { MarketShareChartData } from "@app/overviews/market-expansion-dashboard/shared/MarketShareChartData";

interface PayerCount {
  payerName: string;
  count: number;
}

interface MarketShareChartResult {
  chartData: MarketShareChartData[];
  total: number;
}

const OTHER_THRESHOLD = 0.05;

/**
 * Aggregates payer counts by payer name and buckets any payer under 5% of the total into a
 * single "Other" slice, so that percentages are recalculated off the summed bucket rather than
 * by summing individually-rounded percentages. Shared across lines of business — callers
 * normalize their own payer/metric shape (enrollment, plan count, etc.) into {payerName, count}
 * before calling this.
 */
function buildMarketShareChartData(payerCounts: PayerCount[]): MarketShareChartResult {
  const aggregatedPayerData = new Map<string, number>();

  for (const payer of payerCounts) {
    aggregatedPayerData.set(payer.payerName, (aggregatedPayerData.get(payer.payerName) ?? 0) + payer.count);
  }

  const total = Array.from(aggregatedPayerData.values()).reduce((sum, val) => sum + val, 0);

  if (total === 0) {
    return { chartData: [], total: 0 };
  }

  const sorted = Array.from(aggregatedPayerData.entries()).sort((a, b) => b[1] - a[1]);

  // Payers with at least 5% of the total get their own slice of the pie and the remaining are rolled up under "Other"
  const significantPayersBucket = sorted.filter(([, count]) => count / total >= OTHER_THRESHOLD);
  const otherPayersBucket = sorted.filter(([, count]) => count / total < OTHER_THRESHOLD);

  const chartData: MarketShareChartData[] = significantPayersBucket.map(([payerName, count], index) => ({
    count,
    fillColor: MARKET_SHARE_COLORS[index % MARKET_SHARE_COLORS.length].sliceColor,
    name: payerName,
    textColor: MARKET_SHARE_COLORS[index % MARKET_SHARE_COLORS.length].textColor,
  }));

  if (otherPayersBucket.length > 0) {
    const otherTotal = otherPayersBucket.reduce((sum, [, count]) => sum + count, 0);
    chartData.push({
      count: otherTotal,
      fillColor: MARKET_SHARE_COLORS_OTHER.sliceColor,
      includedPayers: otherPayersBucket.map(x => x[0]),
      name: "Other",
      textColor: MARKET_SHARE_COLORS_OTHER.textColor,
    });
  }

  return { chartData, total };
}

export { buildMarketShareChartData, MarketShareChartResult, PayerCount };
